import { Search, Bell, Home, Users, MessageSquare, Calendar, MapPin, Check, Star, ShieldCheck } from 'lucide-react';

/**
 * Feb 2026 Marketing Redesign — Hero product mockup.
 *
 * A pure Tailwind/HTML mock of the actual ShiftCover365 manager UI.
 * Composition:
 *   • A subtle laptop-like frame (rounded, soft shadow) containing a
 *     narrow sidebar + a "Post a shift" pill CTA + a shift-record card.
 *   • An overlapping floating worker-profile card on the right showing
 *     a reliability score and verified tick — the design-agent's brief.
 *
 * No analytics graphs, no purple gradients, no lorem-ipsum. Numbers and
 * copy are realistic without being fake customer names.
 */

export default function ProductMockup() {
  return (
    <div className="relative w-full max-w-[560px]" data-testid="hero-product-mockup">
      {/* Ambient glow */}
      <div className="absolute -inset-8 bg-gradient-to-br from-[#DA291C]/[0.06] via-transparent to-[#0B132B]/[0.04] rounded-[40px] blur-2xl -z-10" />

      {/* Main manager UI card */}
      <div className="relative rounded-3xl bg-white border border-slate-200/80 shadow-[0_20px_60px_-15px_rgba(11,19,43,0.15)] overflow-hidden">
        <div className="flex h-[440px]">
          {/* Sidebar */}
          <div className="w-16 bg-[#111A3A] flex flex-col items-center py-6 gap-4 shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_c81cb521-10eb-499a-aced-0c520ab93005/artifacts/vg7t80ao_ShiftCover365%20-%20simple%20logo%20cropped.png"
              alt="ShiftCover365"
              className="h-8 w-8 rounded-md object-contain bg-white/5 p-1"
            />
            <div className="flex-1" />
            {[
              { icon: Home, active: true },
              { icon: Calendar, active: false },
              { icon: Users, active: false },
              { icon: MessageSquare, active: false },
              { icon: Bell, active: false },
            ].map((item, i) => (
              <div
                key={i}
                className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                  item.active ? 'bg-white/15 text-white' : 'text-slate-400'
                }`}
              >
                <item.icon className="h-4 w-4" strokeWidth={1.8} />
              </div>
            ))}
            <div className="flex-1" />
          </div>

          {/* Content */}
          <div className="flex-1 p-6 bg-[#FAFBFC]">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Home</p>
                <p className="text-[15px] font-bold text-[#0B132B] mt-0.5">Good morning</p>
              </div>
              <button className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#DA291C] text-white text-[12.5px] font-semibold shadow-md shadow-red-900/20">
                <Search className="h-3.5 w-3.5" />
                Post a shift
              </button>
            </div>

            {/* Shift card */}
            <div className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13.5px] font-bold text-[#0B132B]">Counter cover</p>
                  <div className="flex items-center gap-1 mt-1 text-[11.5px] text-slate-500">
                    <MapPin className="h-3 w-3" />
                    <span>Shoreditch branch</span>
                  </div>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
                  Open
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Wednesday</p>
                  <p className="text-[13px] font-semibold text-[#0B132B] mt-0.5">09:00 — 17:00</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Rate</p>
                  <p className="text-[13px] font-semibold text-[#0B132B] mt-0.5">£15/hr</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-1.5">
                    {['#DA291C', '#0B132B', '#0EA5E9'].map((c, i) => (
                      <div
                        key={i}
                        className="h-6 w-6 rounded-full border-2 border-white"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <span className="text-[12px] text-slate-600 ml-1">3 applicants</span>
                </div>
                <span className="text-[12px] font-semibold text-[#0B132B]">Review →</span>
              </div>
            </div>

            {/* Second, smaller shift row */}
            <div className="mt-3 rounded-2xl bg-white border border-slate-200 p-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[12.5px] font-semibold text-[#0B132B]">Weekend cover</p>
                <p className="text-[10.5px] text-slate-500 mt-0.5">Saturday · 10:00 — 16:00</p>
              </div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wide">
                Draft
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating worker profile card */}
      <div
        className="absolute -bottom-8 -right-4 sm:-right-10 w-[240px] rounded-2xl bg-white border border-slate-200/90 shadow-[0_20px_50px_-15px_rgba(11,19,43,0.2)] p-4 hidden sm:block"
        data-testid="hero-worker-profile"
      >
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#0B132B] to-[#1A2238] flex items-center justify-center text-white text-sm font-bold shrink-0">
            AR
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <p className="text-[13px] font-bold text-[#0B132B] truncate">Amina R.</p>
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" strokeWidth={2.5} />
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">Retail · 3 yrs</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Star className="h-3.5 w-3.5 text-amber-500" fill="currentColor" />
            <span className="text-[12px] font-semibold text-[#0B132B]">4.9</span>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-bold">
            <Check className="h-3 w-3" strokeWidth={3} /> 98% reliable
          </span>
        </div>
      </div>
    </div>
  );
}
